import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { BarChart3, FileSpreadsheet, FileText, UserX } from "lucide-react";
import api, { downloadFile } from "@/lib/api";
import ExportDialog from "@/components/ExportDialog";

const DAY_AR = ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];
const dayName = (d) => {
  const dt = new Date(`${d}T00:00:00`);
  return Number.isNaN(dt.getTime()) ? "—" : DAY_AR[dt.getDay()];
};
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/ui/date-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const ST_AR = { present: "حاضر", late: "متأخر", absent: "غائب", excused: "مستأذن" };

const flag = (m) => {
  if (m >= 60) return { label: "حرج +60", cls: "bg-rose-50 text-rose-700 border-rose-200" };
  if (m >= 30) return { label: "مرتفع +30", cls: "bg-amber-50 text-amber-700 border-amber-200" };
  if (m >= 15) return { label: "متوسط +15", cls: "bg-yellow-50 text-yellow-700 border-yellow-200" };
  return { label: "منخفض", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
};

const readSavedDateFilters = () => {
  try { return JSON.parse(localStorage.getItem("lateness_date_filters") || "{}"); } catch { return {}; }
};

const COLS = [
  { key: "employee_name", label: "اسم الموظف" },
  { key: "employee_id", label: "رقم الهوية" },
  { key: "days", label: "أيام العمل" },
  { key: "late_days", label: "أيام التأخير" },
  { key: "total_late_minutes", label: "دقائق التأخير" },
  { key: "early_days", label: "أيام الانصراف المبكر" },
  { key: "total_early_minutes", label: "دقائق الانصراف المبكر" },
  { key: "no_checkout_days", label: "أيام الانصراف بلا بصمة" },
  { key: "total_minutes", label: "مجموع الدقائق" },
  { key: "absent_days", label: "أيام غياب" },
  { key: "excused_days", label: "استئذان" },
  { key: "flag", label: "المؤشر" },
];

export default function LatenessTab() {
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [employee, setEmployee] = useState("all");
  const [employeeNames, setEmployeeNames] = useState([]);
  const [from, setFrom] = useState(() => readSavedDateFilters().from || dayjs().startOf("month").format("YYYY-MM-DD"));
  const [to, setTo] = useState(() => readSavedDateFilters().to || dayjs().format("YYYY-MM-DD"));
  const [loading, setLoading] = useState(false);
  const [exportKind, setExportKind] = useState(null);

  useEffect(() => {
    localStorage.setItem("lateness_date_filters", JSON.stringify({ from, to }));
  }, [from, to]);

  const [sort, setSort] = useState({ key: null, dir: "desc" });
  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" }));

  useEffect(() => {
    api.get("/attendance/employees").then((r) =>
      setEmployeeNames(r.data.map((e) => e.employee_name))).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    setDetail(null);
    const req = employee === "all"
      ? api.get("/reports/lateness", { params: { frm: from, to } }).then((r) => setRows(r.data))
      : api.get("/reports/employee", { params: { name: employee, frm: from, to } }).then((r) => setDetail(r.data));
    req.catch(() => toast.error("تعذر تحميل التقرير")).finally(() => setLoading(false));
  }, [from, to, employee]);

  const exportReport = async (kind, names) => {
    if (employee === "all" && names === undefined) { setExportKind(kind); return; }
    try {
      const empParam = employee !== "all" ? `&employee=${encodeURIComponent(employee)}`
        : names ? `&employees=${encodeURIComponent(names.join(","))}` : "";
      const report = employee !== "all" ? "employee" : "lateness";
      await downloadFile(`/export/${kind}?report=${report}&frm=${from}&to=${to}${empParam}`,
        `تقرير-التأخر-${employee !== "all" ? employee : "الكل"}-${from}.${kind === "excel" ? "xlsx" : "pdf"}`);
      toast.success("تم تصدير التقرير");
      setExportKind(null);
    } catch { toast.error("فشل التصدير"); }
  };

  const exportAbsence = async (names) => {
    if (employee === "all" && names === undefined) { setExportKind("absence"); return; }
    try {
      const empParam = employee !== "all" ? `&employee=${encodeURIComponent(employee)}`
        : names ? `&employees=${encodeURIComponent(names.join(","))}` : "";
      await downloadFile(`/export/pdf?report=absence&frm=${from}&to=${to}${empParam}`,
        `تقرير-متابعة-الغياب-${from}.pdf`);
      toast.success("تم تصدير التقرير");
      setExportKind(null);
    } catch { toast.error("فشل التصدير"); }
  };

  const exportStats = async (names) => {
    if (employee === "all" && names === undefined) { setExportKind("stats"); return; }
    try {
      const empParam = employee !== "all" ? `&employee=${encodeURIComponent(employee)}`
        : names ? `&employees=${encodeURIComponent(names.join(","))}` : "";
      await downloadFile(`/export/pdf?report=stats&frm=${from}&to=${to}${empParam}`,
        `إحصائية-الدوام-${employee !== "all" ? employee : "الكل"}-${from}.pdf`);
      toast.success("تم تصدير الإحصائية");
      setExportKind(null);
    } catch { toast.error("فشل التصدير"); }
  };

  const sortedRows = (() => {
    if (!sort.key) return rows;
    const val = (a) =>
      sort.key === "total_minutes" ? (a.total_late_minutes || 0) + (a.total_early_minutes || 0)
        : sort.key === "flag" ? (a.total_late_minutes || 0)
          : a[sort.key];
    return [...rows].sort((x, y) => {
      const vx = val(x) ?? "", vy = val(y) ?? "";
      const cmp = typeof vx === "number" && typeof vy === "number"
        ? vx - vy : String(vx).localeCompare(String(vy), "ar");
      return sort.dir === "desc" ? -cmp : cmp;
    });
  })();

  return (
    <div className="bg-white rounded-2xl border shadow-sm" data-testid="lateness-tab">
      <ExportDialog open={!!exportKind} onClose={() => setExportKind(null)}
        onConfirm={(names) => exportKind === "absence" ? exportAbsence(names)
          : exportKind === "stats" ? exportStats(names) : exportReport(exportKind, names)}
        title={exportKind === "absence" ? "تقرير متابعة الغياب"
          : exportKind === "stats" ? "إحصائية الدوام" : "تقرير التأخر والخروج المبكر"} />
      <div className="p-4 border-b flex flex-wrap items-center gap-3">
        <Select value={employee} onValueChange={setEmployee}>
          <SelectTrigger data-testid="lateness-employee-select" className="w-56 h-9 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="lateness-select-all">تحديد الكل</SelectItem>
            {employeeNames.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <DateInput type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            data-testid="lateness-date-from" className="w-36 h-9 text-xs" dir="ltr" />
          <span className="text-xs text-slate-400">إلى</span>
          <DateInput type="date" value={to} onChange={(e) => setTo(e.target.value)}
            data-testid="lateness-date-to" className="w-36 h-9 text-xs" dir="ltr" />
        </div>
        <div className="mr-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={() => exportReport("excel")}
            data-testid="lateness-export-excel" className="h-9 text-xs gap-1.5">
            <FileSpreadsheet className="w-4 h-4 text-emerald-700" /> Excel
          </Button>
          <Button size="sm" variant="outline" onClick={() => exportReport("pdf")}
            data-testid="lateness-export-pdf" className="h-9 text-xs gap-1.5">
            <FileText className="w-4 h-4 text-rose-700" /> PDF
          </Button>
          <Button size="sm" variant="outline" onClick={() => exportAbsence()}
            data-testid="absence-report-export-pdf" className="h-9 text-xs gap-1.5">
            <UserX className="w-4 h-4 text-rose-700" /> متابعة الغياب
          </Button>
          <Button size="sm" variant="outline" onClick={() => exportStats()}
            data-testid="stats-export-pdf" className="h-9 text-xs gap-1.5">
            <BarChart3 className="w-4 h-4 text-emerald-700" /> إحصائية الدوام
          </Button>
        </div>
      </div>

      {loading && <p className="text-center py-10 text-slate-400">جارٍ التحميل...</p>}

      {!loading && employee === "all" && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="table-lateness-report">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs">
                {COLS.map((c) => (
                  <th key={c.key + c.label} className="px-4 py-3 font-semibold text-center whitespace-nowrap">
                    <button type="button" data-testid={`sort-${c.key}`} onClick={() => toggleSort(c.key)}
                      className="inline-flex items-center gap-1 hover:text-emerald-700 transition-colors">
                      {c.label}
                      {sort.key === c.key && <span className="text-[10px]">{sort.dir === "desc" ? "▼" : "▲"}</span>}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 &&
                <tr><td colSpan={12} className="text-center py-10 text-slate-400">لا توجد بيانات</td></tr>}
              {sortedRows.map((a, i) => {
                const f = flag(a.total_late_minutes);
                return (
                  <tr key={a.employee_name} className="border-t hover:bg-slate-50/60">
                    <td className="px-4 py-2.5 font-semibold whitespace-nowrap text-center">{a.employee_name}</td>
                    <td className="px-4 py-2.5 text-slate-500 text-center">{a.employee_id || "—"}</td>
                    <td className="px-4 py-2.5 text-center">{a.days}</td>
                    <td className="px-4 py-2.5 text-center">{a.late_days}</td>
                    <td className="px-4 py-2.5 font-bold text-amber-700 text-center">{a.total_late_minutes} د</td>
                    <td className="px-4 py-2.5 text-center">{a.early_days}</td>
                    <td className="px-4 py-2.5 font-bold text-orange-700 text-center">{a.total_early_minutes} د</td>
                    <td className="px-4 py-2.5 text-center" data-testid={`no-checkout-days-${i}`}>{a.no_checkout_days || 0}</td>
                    <td className="px-4 py-2.5 font-bold text-rose-700 text-center" data-testid={`total-minutes-${i}`}>{(a.total_late_minutes || 0) + (a.total_early_minutes || 0)} د</td>
                    <td className="px-4 py-2.5 text-center">{a.absent_days}</td>
                    <td className="px-4 py-2.5 text-center">{a.excused_days}</td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`text-[11px] font-semibold border rounded-full px-2.5 py-0.5 ${f.cls}`}>{f.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && employee !== "all" && detail && (
        <div className="p-4 space-y-4" data-testid="lateness-employee-detail">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { l: "أيام الحضور", v: detail.totals.present_days, c: "text-emerald-700" },
              { l: "أيام الغياب", v: detail.totals.absent_days, c: "text-rose-700" },
              { l: "التأخير", v: `${detail.totals.late_days} أيام / ${detail.totals.total_late_minutes} د`, c: "text-amber-700" },
              { l: "الخروج المبكر", v: `${detail.totals.early_days} مرات / ${detail.totals.total_early_minutes} د`, c: "text-orange-700" },
            ].map((x) => (
              <div key={x.l} className="border rounded-xl p-3 text-center">
                <p className="text-[11px] text-slate-400">{x.l}</p>
                <p className={`font-extrabold ${x.c}`}>{x.v}</p>
              </div>
            ))}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="table-employee-lateness">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-xs">
                  {["التاريخ", "اليوم", "الحضور", "الانصراف", "تأخير (د)", "خروج مبكر (د)", "الحالة"].map((h) => (
                    <th key={h} className="px-3 py-3 font-semibold text-center">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {detail.records.length === 0 &&
                  <tr><td colSpan={7} className="text-center py-10 text-slate-400">لا توجد سجلات لهذا الموظف في هذه الفترة</td></tr>}
                {detail.records.map((r) => (
                  <tr key={r.date} className="border-t">
                    <td className="px-3 py-2 font-mono text-xs text-center" dir="ltr">{r.date}</td>
                    <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap text-center">{dayName(r.date)}</td>
                    <td className="px-3 py-2 font-mono text-xs text-center" dir="ltr">{r.check_in || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs text-center" dir="ltr">{r.check_out || "—"}</td>
                    <td className="px-3 py-2 text-center">{r.late_minutes > 0 ? <span className="text-amber-700 font-bold">{r.late_minutes}</span> : "—"}</td>
                    <td className="px-3 py-2 text-center">{r.early_minutes > 0 ? <span className="text-orange-700 font-bold">{r.early_minutes}</span> : "—"}</td>
                    <td className="px-3 py-2 text-center">{ST_AR[r.status]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
